import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  addToCart,
  removeCartItem,
  serviceShopCartQuery,
  serviceShopOfferQuery,
  serviceShopQuery,
  setCartQuantity,
  submitCart,
  type ShopCartPage,
  type ShopItem,
  type ShopOffer,
  type ShopWriteResult,
} from '../api/easeedeskServiceShop'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Modal } from '../ui/Modal'
import { newAlert } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import './ServiceShopPage.css'

/**
 * "Service shop" - `Service_shop::index()`, macheta
 * `views/version-3/easeedesk/service_shop/index.php`, cu fisa serviciului din
 * `details.php` si cosul din `cart.php`.
 *
 * Carduri, nu un tabel. Un tabel raspunde la "care sunt toate"; aici intrebarea
 * e "de ce am nevoie", iar la aia raspund o pictograma, un nume si o propozitie.
 * Aceleasi clase (`shop-grid`, `shop-card`, `shop-icon`, ...) si aceleasi doua
 * foi de stil ca in portal, ca ecranul sa arate la fel, nu doar sa spuna la fel.
 *
 * "ADD TO CART" LIPSEA, SI DE ASTA
 * --------------------------------
 * La mutare, cele patru scrieri ale magazinului (`add`, `setQuantity`,
 * `removeItem`, `submit`) au fost lasate dinadins in portalul vechi, si ecranul
 * nici nu le desena butoanele - "unul care pare ca adauga in cos si nu adauga e
 * mai rau decat lipsa lui". Intre timp portalul vechi a fost scos de pe server,
 * si ce ramasese era un magazin din care nu se poate cumpara nimic.
 *
 * Trei din patru s-au intors: adaugarea (de pe card SI de pe fisa, cu cantitate
 * si nota), cantitatea si stergerea unui rand. Ele merg acum la api-ul nou -
 * `POST /api/v2/easeedesk/service-shop/cart/items...` - care cere acelasi drept
 * si isi ia compania din jeton, nu din formular.
 *
 * TRIMITEREA a venit si ea, cu tot ce avea in `cart.php`: "who is it for",
 * prioritatea si justificarea obligatorie, si "Submit request" in subsolul
 * ferestrei. Din ea ies TICHETELE cererii - parintele, serviciile de care
 * depinde si pasii lui - fiecare in prima stare a fluxului tipului sau.
 *
 * SI FEREASTRA COSULUI NU NASTE UN COS. In CI3, deschiderea ei chema
 * `Service_requests::cart()`, care INSEREAZA un rand cand omul n-are niciunul -
 * o scriere in baza pe o simpla privire. Aici, fara cos se vede un cos gol;
 * randul se naste la prima apasare pe "Add to cart".
 *
 * Textul scris de om - numele serviciului, descrierea, numele categoriei - trece
 * prin JSX, care il scapa singur; nicaieri pe ecranul asta nu se pune HTML brut
 * (`dangerouslySetInnerHTML`), deci nu e nevoie de un `esc()` ca in tabelele
 * facute cu DataTables.
 */
export function ServiceShopPage() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState(0)
  const [offer, setOffer] = useState(0)
  const [cartOpen, setCartOpen] = useState(false)
  const queryClient = useQueryClient()

  /* `keepPreviousData`: fara el, fiecare litera tastata in casuta de cautare
     golea `page.data`, pagina cadea pe rotita si casuta isi pierdea focusul -
     adica exact ce se intampla cand scrii al doilea caracter. */
  const page = useQuery({ ...serviceShopQuery(search, category), placeholderData: keepPreviousData })
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/adm.css', 'css/service_shop.css'], shell.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const offers = page.data.offers
  const withApproval = offers.filter((one) => one.requires_approval).length

  return (
    <div id="content" className="od-service-shop">
      <PageHead
        title="Service shop"
        icon="fa-store"
        tone="blue-hoki"
        note="Ask for a service. Each item opens a ticket you can follow."
        stats={[
          { value: offers.length, label: 'available', tone: 'blue-hoki' },
          { value: withApproval, label: 'flagged for approval', tone: 'yellow' },
        ]}
      />

      <ShopTabs active="shop" cartCount={page.data.cartCount} canCatalog={page.data.canCatalog} onCart={() => setCartOpen(true)} />

      <div className="adm-bar">
        <div className="adm-field">
          <label htmlFor="shopSearch">Search</label>
          <input
            type="text"
            id="shopSearch"
            className="form-control input-sm"
            placeholder="What do you need?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="adm-field">
          <label htmlFor="shopCategory">Category</label>
          <select
            id="shopCategory"
            className="form-control input-sm"
            value={category}
            onChange={(e) => setCategory(Number(e.target.value))}
          >
            <option value={0}>All categories</option>
            {page.data.categories.map((one) => (
              <option value={one.id} key={one.id}>
                {one.name} ({one.total})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="shop-grid" id="shopGrid">
        {offers.length === 0 && (
          <div className="shop-empty">
            <i className="fas fa-store-slash"></i>
            <strong>Nothing to request yet</strong>
            {/*
              Golul are doua cauze si spune care sunt, pentru ca omul care il
              vede nu poate repara niciuna si trebuie sa stie pe cine sa intrebe.
              Un "no results" simplu l-ar face sa creada ca a gresit cautarea.
            */}
            <p>
              Either nothing matches what you typed, or the catalogue has not been published for your company yet. A
              service shows up here once it is published to customers <em>and</em> has a ticket type set.
            </p>
          </div>
        )}

        {offers.map((one) => (
          <OfferCard one={one} onOpen={() => setOffer(one.ID)} queryClient={queryClient} key={one.ID} />
        ))}
      </div>

      {/*
        Locul mesajelor, ca in macheta: `#shopWarning`, sub grila. Mesajele merg
        acolo si nu intr-un toast din coltul ecranului fiindca acolo le punea
        portalul, iar ochiul celui care tocmai a apasat e in pagina, nu in colt.
      */}
      <div id="shopWarning" className="adm-warning"></div>

      <OfferModal offer={offer} onClose={() => setOffer(0)} queryClient={queryClient} />
      <CartModal open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  )
}

/**
 * `version-3/softsee/adm/_pagehead`, partea folosita de magazin: tonul, iconita,
 * titlul, propozitia si cifrele din dreapta.
 */
export function PageHead({ title, icon, tone, note, stats }: {
  title: string
  icon: string
  tone: string
  note: string
  stats: { value: number; label: string; tone: string }[]
}) {
  return (
    <div className={`adm-page-head adm-tone-${tone}`}>
      <span className="adm-page-icon">
        <i className={`fas ${icon}`}></i>
      </span>
      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>{title}</h3>
        </div>
        <span className="adm-page-note">{note}</span>
      </div>
      <div className="adm-page-stats">
        {stats.map((one) => (
          <div className={`adm-stat adm-stat-${one.tone}`} key={one.label}>
            <span className="adm-stat-value">{one.value}</span>
            <span className="adm-stat-label">{one.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * `_tabs.php`: cele trei locuri ale catalogului si cosul, la dreapta.
 *
 * Fila "Service Catalog" - administrarea - atarna de ALT drept decat magazinul:
 * `inventoree.service_catalog.read`, nu `easeedesk.service_shop.read`. Unul e
 * "am voie sa cer un serviciu", celalalt "am voie sa schimb ce servicii
 * exista"; cine are numai primul nu vede fila deloc, exact ca in portal.
 *
 * Raspunsul vine de la server (`canCatalog`), fiindca ecranul nu stie ce
 * drepturi are omul. Ecranul catalogului si-a primit adresa in /app pe 10
 * septembrie 2026 - pana atunci fila nu se desena, ca sa nu duca inapoi in
 * portalul vechi din mijlocul lui v4.
 */
export function ShopTabs({ active, cartCount, canCatalog, onCart }: {
  active: 'shop' | 'requests'
  cartCount: number
  canCatalog?: boolean
  onCart(): void
}) {
  return (
    <div className="shop-tabs">
      {canCatalog && (
        <a className="shop-tab" href="/app/service_catalog">
          <i className="fas fa-sitemap"></i> Service Catalog
        </a>
      )}

      <a className={`shop-tab${active === 'shop' ? ' is-on' : ''}`} href="/app/service_shop">
        <i className="fas fa-store"></i> Service Catalog Shop
      </a>

      <a className={`shop-tab${active === 'requests' ? ' is-on' : ''}`} href="/app/service_shop/requests">
        <i className="fas fa-clipboard-list"></i> My requests
      </a>

      {/* Cosul sta la dreapta, despartit de restul: nu e un al treilea loc unde
          te duci sa te uiti, e ce ai strans pana acum. */}
      <span style={{ flex: 1 }}></span>

      <button type="button" className="shop-tab" id="shopCartOpen" onClick={onCart}>
        <i className="fas fa-shopping-cart"></i> Cart
        <span className="shop-tab-count" id="shopCartCount" data-count={cartCount}>
          {cartCount}
        </span>
      </button>
    </div>
  )
}

/* ===================================================================== *
 *                     SCRIERILE, INTR-UN SINGUR LOC
 * ===================================================================== */

/**
 * Un mesaj in `#shopWarning`, exact ca `say()` din `public/js/service_shop.js`.
 *
 * Pe ecranul "My requests", care imprumuta fereastra cosului, `#shopWarning` nu
 * exista - si atunci `newAlert` cade singur pe `.notifications`, locul in care
 * portalul pune orice mesaj scurt. Nu ramane nimeni fara raspuns.
 *
 * Textul vine cu raspunsul si nu se scrie aici: doar serverul stie daca randul
 * a fost adaugat sau doar i-a crescut cantitatea. Cele doua locuri care si-l
 * scriau singure in portal ajunsesera sa spuna "undefined" pe fisa serviciului.
 */
function say(message: string, kind: 'success' | 'danger' = 'success'): void {
  newAlert(
    `<i class="fas fa-${kind === 'danger' ? 'exclamation-circle' : 'check-circle'}"></i> ` + escapeHtml(message),
    kind,
    4500,
    document.querySelector('#shopWarning')
  )
}

/**
 * `newAlert` pune HTML, nu text - asa e scrisa in portal, si tot asa o cheama
 * si CMDB. Mesajele de aici vin de la serverul nostru, dar unul dintre ele
 * poarta cuvinte pe care nu le-am scris noi (numele unui serviciu, intr-o
 * eroare), deci textul se scapa inainte, nu dupa.
 */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] as string
  )
}

/**
 * O scriere dusa pana la capat: cheama capatul, spune ce s-a intamplat, si cere
 * inapoi TOT ce tine de magazin.
 *
 * Reimprospatarea se face prin cheia de prefix `['easeedesk', 'service-shop']`,
 * adica si catalogul (pentru numarul de langa cos), si cosul, si lista de
 * cereri. Portalul misca numarul cu mana, din raspuns; aici il misca datele,
 * si atunci nu poate ramane un numar scris in ecran care sa nu mai fie cel din
 * baza - felul in care badge-ul ala ramanea pe 1 in portal.
 */
async function write(
  queryClient: QueryClient,
  run: () => Promise<ShopWriteResult>,
  after?: (result: ShopWriteResult) => void
): Promise<void> {
  try {
    const result = await run()
    if (result.message !== '') say(result.message)
    after?.(result)
  } catch (error) {
    /* `complete` in loc de `success` + `error`, ca in portal: orice sfarsit -
       si 400, si 404, si un raspuns care nu e json - trebuie sa spuna ceva si
       sa deblocheze butonul, altfel omul crede ca aplicatia a murit. */
    say(error instanceof ApiError ? error.message : 'The server did not answer.', 'danger')
  } finally {
    await queryClient.invalidateQueries({ queryKey: ['easeedesk', 'service-shop'] })
  }
}

/* ===================================================================== *
 *                              CARDURILE
 * ===================================================================== */

function OfferCard({ one, onOpen, queryClient }: { one: ShopOffer; onOpen(): void; queryClient: QueryClient }) {
  const [busy, setBusy] = useState(false)

  const add = (ev: { stopPropagation(): void }) => {
    /* Cardul intreg se deschide ca fisa; butonul din el adauga direct. Apasarea
       pe buton nu trebuie sa faca amandoua - `e.stopPropagation()` in portal. */
    ev.stopPropagation()
    if (busy) return
    setBusy(true)
    void write(queryClient, () => addToCart(one.ID, 1, '')).finally(() => setBusy(false))
  }

  return (
    /*
     * Cardul se deschide la apasare, ca in portal. Acolo era un `div` si atat;
     * aici primeste si tastatura - `role`, `tabIndex`, Enter si Space - fiindca
     * asta nu schimba nimic din cum arata, si un drum care exista numai pentru
     * mouse nu e un drum pentru toata lumea.
     */
    <div
      className="shop-card adm-tone-blue-hoki"
      data-id={one.ID}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault()
          onOpen()
        }
      }}
    >
      <div className="shop-card-top">
        {/* Imaginea serviciului (cerinta CEC 33) ia locul iconitei cand exista. */}
        <span className={`shop-icon${one.image_url ? ' shop-icon-img' : ''}`}>
          {one.image_url ? (
            <img src={one.image_url} alt="" loading="lazy" />
          ) : (
            <i className={`fas ${one.icon || 'fa-cube'}`}></i>
          )}
        </span>

        <div className="shop-card-title">
          <h4>{one.name}</h4>
          {one.category_name !== '' && <span className="shop-card-cat">{one.category_name}</span>}
        </div>
      </div>

      <p className="shop-card-desc">{one.short_description || 'No description was written for this service.'}</p>

      <div className="shop-card-foot">
        {one.requires_approval && (
          <span className="shop-approval" title="Somebody has to approve it first">
            <i className="fas fa-user-check"></i> Needs approval
          </span>
        )}

        <span style={{ flex: 1 }}></span>

        <button type="button" className="btn btn-info btn-sm shopAdd" data-id={one.ID} disabled={busy} onClick={add}>
          <i className="fas fa-plus"></i> Add to cart
        </button>
      </div>
    </div>
  )
}

/* ===================================================================== *
 *                          FISA SERVICIULUI
 * ===================================================================== */

/** `details.php`: descrierea, cifrele, stadiile, cantitatea si nota. */
function OfferModal({ offer, onClose, queryClient }: { offer: number; onClose(): void; queryClient: QueryClient }) {
  const q = useQuery(serviceShopOfferQuery(offer))
  const data = q.data

  const [quantity, setQuantity] = useState('1')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const close = () => {
    /* Casutele se golesc la inchidere: fereastra e una singura pentru toate
       serviciile, iar o nota ramasa de la serviciul dinainte ar pleca pe
       urmatorul fara ca cineva s-o vada. */
    setQuantity('1')
    setNote('')
    onClose()
  }

  const add = () => {
    if (busy || !data) return
    setBusy(true)
    void write(queryClient, () => addToCart(data.offer.ID, Number(quantity) || 1, note), () => close())
      .finally(() => setBusy(false))
  }

  const cost =
    data && data.offer.estimate_unit_cost !== null && data.offer.estimate_unit_cost > 0
      ? `${data.offer.estimate_unit_cost.toFixed(2)} ${data.offer.currency}`.trim()
      : 'No charge'

  return (
    <Modal
      id="shopModal"
      open={offer > 0}
      onClose={close}
      labelledBy="shopModalLabel"
      title={
        data ? (
          <>
            <i className={`fas ${data.offer.icon || 'fa-cube'}`}></i> {data.offer.name}
            {data.offer.category_name !== '' && <span className="shop-card-cat"> {data.offer.category_name}</span>}
          </>
        ) : (
          'Service'
        )
      }
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={close}>
            Keep looking
          </button>
          <button
            type="button"
            className="btn btn-info"
            id="shopDetailAdd"
            data-id={data?.offer.ID ?? 0}
            disabled={busy || !data}
            onClick={add}
          >
            <i className="fas fa-plus"></i> Add to cart
          </button>
        </>
      }
    >
      {!data ? (
        <Spinner />
      ) : (
        <>
          {data.offer.image_url && <img className="shop-detail-img" src={data.offer.image_url} alt="" />}
          <p>{data.offer.long_description || data.offer.short_description || 'No description was written for this service.'}</p>

          {/* Cifrele intr-o lista de definitii, nu in proza: se citesc dintr-o
              privire si stau una sub alta oricat de lung ar fi numele SLA-ului. */}
          <dl className="dl-horizontal" style={{ marginBottom: '14px' }}>
            <dt>Cost</dt>
            <dd>{cost}</dd>

            <dt>Target</dt>
            <dd>{data.offer.sla_name || '—'}</dd>

            <dt>Configuration item</dt>
            <dd>CI-{data.offer.ci_id}</dd>
          </dl>

          <h5 style={{ fontWeight: 600 }}>What happens next</h5>

          {data.stages.length === 0 ? (
            /* Cand tipul n-are flux, se spune asta: e o configurare care
               lipseste si se poate repara, nu o propozitie despre serviciu. */
            <p className="text-muted">
              The ticket type of this service has no workflow yet, so the stages cannot be shown. The service can still
              be requested.
            </p>
          ) : (
            <ol className="shop-stages">
              {data.stages.map((stage, index) => (
                <li key={`${stage.name}-${index}`}>
                  {stage.colour !== null && <span className="shop-stage-dot" style={{ background: stage.colour }}></span>}
                  {stage.name}
                  {/* Cine preia nu se arata: e ecranul celui care cere, si el vrea
                      sa stie prin ce trece cererea, nu care echipa interna o ia la
                      fiecare pas. La fel ca in catalog. */}
                </li>
              ))}
            </ol>
          )}

          <hr />

          <div className="form-group">
            <label className="control-label" htmlFor="shopDetailQty">
              Quantity
            </label>
            <input
              type="number"
              className="form-control"
              id="shopDetailQty"
              min={1}
              max={99}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>

          {/* Optionala aici, spre deosebire de justificarea de la trimitere: aia
              e "de ce am nevoie" si o cere cel care decide, asta e "ce mai
              trebuie stiut despre bucata asta". */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="control-label" htmlFor="shopDetailNote">
              Additional note <span className="text-muted" style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              className="form-control"
              id="shopDetailNote"
              rows={3}
              placeholder="Anything the team should know about this one."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            ></textarea>
          </div>
        </>
      )}
    </Modal>
  )
}

/* ===================================================================== *
 *                                COSUL
 * ===================================================================== */

/**
 * `cart.php`, cap la cap: randurile, totalul, cele trei casute si trimiterea.
 *
 * Starea formularului sta AICI, nu in corp: "Submit request" e in subsolul
 * ferestrei, ca in macheta, iar un buton care trimite ce a scris omul trebuie
 * sa fie in acelasi loc cu ce a scris.
 */
export function CartModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const q = useQuery({ ...serviceShopCartQuery(), enabled: open })
  const queryClient = useQueryClient()

  const [beneficiary, setBeneficiary] = useState('')
  const [priority, setPriority] = useState('')
  const [note, setNote] = useState('')
  const [noteWrong, setNoteWrong] = useState(false)
  const [busy, setBusy] = useState(false)

  const inCart = (q.data?.items ?? []).filter((one) => one.status === 1)

  const send = () => {
    /* Justificarea, oprita aici ca mesajul sa apara langa campul care il
       priveste; serverul o cere oricum. Exact ca in `service_shop.js`. */
    if (note.trim() === '') {
      setNoteWrong(true)
      document.getElementById('shopNote')?.focus()
      say('Write why you need this.', 'danger')
      return
    }
    setNoteWrong(false)
    if (busy) return
    setBusy(true)

    void (async () => {
      try {
        const result = await submitCart(note, Number(beneficiary) || 0, Number(priority) || 0)
        onClose()
        say(result.message)
        /* Ce n-a putut fi deschis se spune, rand cu rand. In portal mergea in
           log: omul primea "Sent" si nu afla niciodata ca un serviciu din
           cererea lui n-a produs niciun tichet. */
        for (const problem of result.problems ?? []) say(problem, 'danger')
        setBeneficiary('')
        setPriority('')
        setNote('')
        /* Se pleaca spre "My requests" dupa ce mesajul a apucat sa fie citit,
           ca in portal. */
        window.setTimeout(() => window.location.assign('/app/service_shop/requests'), 1400)
      } catch (error) {
        say(error instanceof ApiError ? error.message : 'The server did not answer.', 'danger')
      } finally {
        await queryClient.invalidateQueries({ queryKey: ['easeedesk', 'service-shop'] })
        setBusy(false)
      }
    })()
  }

  return (
    <Modal
      id="shopCartModal"
      open={open}
      onClose={onClose}
      labelledBy="shopCartModalLabel"
      title={
        <>
          <i className="fas fa-shopping-cart"></i> Your cart
        </>
      }
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Keep looking
          </button>
          {inCart.length > 0 && (
            <button type="button" className="btn btn-info" id="shopSubmit" disabled={busy} onClick={send}>
              <i className="fas fa-paper-plane"></i> Submit request
            </button>
          )}
        </>
      }
    >
      {!q.data ? (
        <Spinner />
      ) : (
        <CartBody
          page={q.data}
          beneficiary={beneficiary}
          onBeneficiary={setBeneficiary}
          priority={priority}
          onPriority={setPriority}
          note={note}
          onNote={setNote}
          noteWrong={noteWrong}
        />
      )}
    </Modal>
  )
}

function CartBody({ page, beneficiary, onBeneficiary, priority, onPriority, note, onNote, noteWrong }: {
  page: ShopCartPage
  beneficiary: string
  onBeneficiary(value: string): void
  priority: string
  onPriority(value: string): void
  note: string
  onNote(value: string): void
  noteWrong: boolean
}) {
  const queryClient = useQueryClient()
  const inCart = page.items.filter((one) => one.status === 1)

  /* Doua monede in acelasi cos nu se aduna. Se spune, in loc sa se afiseze un
     total care nu inseamna nimic. */
  let total = 0
  let currency = ''
  let mixed = false
  for (const item of inCart) {
    if (item.price_at_request === null) continue
    total += item.price_at_request * item.quantity
    if (currency !== '' && currency !== item.currency_at_request) mixed = true
    currency = item.currency_at_request
  }

  if (inCart.length === 0) {
    return (
      <div className="shop-empty" style={{ padding: '30px 10px' }}>
        <i className="fas fa-shopping-cart"></i>
        <strong>Your cart is empty</strong>
        <p>Pick a service from the shop and it will show up here.</p>
      </div>
    )
  }

  return (
    <>
      {inCart.map((item) => (
        <CartLine item={item} queryClient={queryClient} key={item.ID} />
      ))}

      {/*
        Totalul se deseneaza si cand e zero, doar ascuns - ca in macheta: scos
        cu totul, n-ar mai fi avut ce sa se schimbe cand omul creste inapoi o
        cantitate, si randul ar fi ramas fara total.
      */}
      <div className="shop-cart-total" id="shopCartTotal" style={total > 0 ? undefined : { display: 'none' }}>
        <small>Estimated{mixed ? ' (mixed currencies)' : ''}</small>{' '}
        <span className="shop-total-sum">{total.toFixed(2)}</span> {currency}
      </div>

      {/*
        Campurile intr-o coloana, cu aceeasi latime si aceeasi distanta intre
        ele, ca in macheta: altfel fiecare isi duce marginea lui si nu incep la
        aceeasi latime.
      */}
      <div className="shop-cart-form">
        <div className="form-group">
          <label className="control-label" htmlFor="shopBeneficiary">
            Who is it for
          </label>
          {/*
            `selectpicker` cu `data-live-search`, ca in macheta: intr-o firma cu
            trei sute de oameni, o lista fara casuta de cautare nu e o lista, e
            o derulare. "Me" e chiar TITLUL pickerului - valoarea goala -
            fiindca asa se poarta si optiunea cu `value=""` din portal.
          */}
          <SelectPicker
            id="shopBeneficiary"
            name="beneficiary"
            className="form-control"
            title="Me"
            size={10}
            options={page.people.map((one) => ({ value: String(one.id), label: one.name }))}
            value={beneficiary}
            onChange={onBeneficiary}
          />
          {/* Spus, pentru ca schimba pe cine ajunge tichetul - si asta nu se
              vede din formular altfel. */}
          <span className="help-block" style={{ marginBottom: 0 }}>
            The ticket is opened for this person, not for you.
          </span>
        </div>

        {/*
          Prioritatea, aceeasi lista ca in Easeedesk. UNA pe cerere, nu pe
          articol: o cerere pleaca dintr-o singura apasare, cu o singura
          justificare, si tot ce iese din ea sunt tichete nascute in aceeasi
          clipa. "Leave it to the service desk" e alegerea de start dinadins:
          cine nu stie ce sa aleaga nu trebuie sa aleaga.
        */}
        {page.priorities.length > 0 && (
          <div className="form-group">
            <label className="control-label" htmlFor="shopPriority">
              Priority
            </label>
            <SelectPicker
              id="shopPriority"
              name="ticket_priority"
              className="form-control"
              title="Leave it to the service desk"
              size={10}
              options={page.priorities.map((one) => ({ value: String(one.ID), label: one.priority_name }))}
              value={priority}
              onChange={onPriority}
            />
          </div>
        )}

        {/*
          Justificarea, obligatorie: ajunge in descrierea tichetului, adica in
          chiar textul pe care il citeste cel care decide. Un tichet fara ea il
          pune sa intrebe inapoi de ce s-a cerut.
        */}
        <div className={`form-group${noteWrong ? ' has-error' : ''}`}>
          <label className="control-label" htmlFor="shopNote">
            Justification <span aria-hidden="true">*</span>
          </label>
          <textarea
            className="form-control"
            id="shopNote"
            rows={3}
            required
            aria-required="true"
            placeholder="Why you need this."
            value={note}
            onChange={(e) => onNote(e.target.value)}
          ></textarea>
          <span className="help-block" style={{ marginBottom: 0 }}>
            Goes on the ticket, for whoever decides.
          </span>
        </div>
      </div>
    </>
  )
}

/**
 * Un rand din cos.
 *
 * Cantitatea pleaca la server pe `change` - adica la iesirea din casuta sau la
 * Enter - nu pe fiecare tasta, exact ca in portal. O cerere pe fiecare cifra ar
 * fi facut din "12" un drum prin "1", si ar fi rescris randul de doua ori.
 */
function CartLine({ item, queryClient }: { item: ShopItem; queryClient: QueryClient }) {
  const [quantity, setQuantity] = useState(String(item.quantity))
  const [busy, setBusy] = useState(false)

  const commit = () => {
    const wanted = Number(quantity)
    if (busy || !Number.isFinite(wanted) || wanted === item.quantity) return
    setBusy(true)
    void write(queryClient, () => setCartQuantity(item.ID, wanted)).finally(() => setBusy(false))
  }

  const drop = () => {
    if (busy) return
    setBusy(true)
    void write(queryClient, () => removeCartItem(item.ID)).finally(() => setBusy(false))
  }

  return (
    <div className="shop-cart-line" data-item={item.ID} style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <span className="shop-icon shop-icon-sm" style={{ flex: 'none', width: '34px', height: '34px' }}>
        <i className={`fas ${item.icon || 'fa-cube'}`}></i>
      </span>

      <div className="shop-cart-name" style={{ flex: '1 1 auto', minWidth: 0 }}>
        <strong>{item.name_at_request}</strong>
        <span>
          {item.requires_approval ? (
            <>
              <i className="fas fa-user-check"></i> needs approval
            </>
          ) : (
            'goes straight to IT'
          )}
        </span>
      </div>

      {/*
        Latimea sta pe element, nu in foaia de stil: `.form-control` isi duce
        `width: 100%`, iar pe un rand flex aia devine latimea de baza a casutei
        si se intinde peste numele serviciului. Aceeasi nota sta si in macheta.
      */}
      <input
        type="number"
        className="form-control input-sm shop-qty shopQty"
        min={1}
        max={99}
        data-item={item.ID}
        value={quantity}
        disabled={busy}
        onChange={(e) => setQuantity(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        style={{ flex: '0 0 66px', width: '66px', minWidth: '66px', maxWidth: '66px', textAlign: 'center' }}
      />

      {item.price_at_request === null ? (
        <span className="shop-cart-price shop-cart-price-none" style={{ flex: 'none', width: '96px', textAlign: 'right' }}>
          &mdash;
        </span>
      ) : (
        <span
          className="shop-cart-price"
          data-unit={item.price_at_request}
          style={{ flex: 'none', width: '96px', textAlign: 'right' }}
        >
          <span className="shop-line-sum">{(item.price_at_request * item.quantity).toFixed(2)}</span>{' '}
          <small>{item.currency_at_request}</small>
        </span>
      )}

      <button
        type="button"
        className="btn btn-link btn-xs text-danger shopDrop"
        data-item={item.ID}
        title="Remove"
        disabled={busy}
        onClick={drop}
        style={{ flex: 'none' }}
      >
        <i className="fas fa-times"></i>
      </button>
    </div>
  )
}
